import React, { createContext, useContext, useState, useEffect } from 'react';

// Language Context für globale Sprachsteuerung in Clara360
const LanguageContext = createContext();

// Language Provider Komponente
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('de'); // Deutsch als Standard
  
  // Verfügbare Sprachen mit Flaggen und Voice-Codes
  const languages = {
    de: { flag: '🇩🇪', voice: 'de-DE', name: 'Deutsch' },
    es: { flag: '🇪🇸', voice: 'es-ES', name: 'Español' },
    en: { flag: '🇬🇧', voice: 'en-US', name: 'English' }
  };

  // Übersetzungen (aus bestehender translations_corrected.js)
  const translations = {
    de: {
      // Navigation & Layout
      dashboard: 'Dashboard',
      properties: 'Objekte',
      tenants: 'Mieter',
      owners: 'Eigentümer',
      finances: 'Finanzen',
      maintenance: 'Wartung',
      documents: 'Dokumente',
      communication: 'Kommunikation',
      analytics: 'Analysen',
      settings: 'Einstellungen',
      tasks: 'Aufgaben',
      
      // Dashboard KPIs
      rentalIncome: 'Mieteinnahmen',
      units: 'Einheiten',
      occupancy: 'Auslastung',
      monthly: 'Monatlich',
      active: 'Aktiv',
      
      // Recent Activities
      newTenant: 'Neuer Mieter',
      newProperty: 'Neues Objekt',
      newUnit: 'Neue Einheit',
      recentActivities: 'Letzte Aktivitäten',
      
      // Quick Actions
      quickActions: 'Schnellaktionen',
      addTenant: 'Mieter Hinzufügen',
      addProperty: 'Objekt Hinzufügen',
      uploadDocument: 'Dokument Hochladen',
      reportIssue: 'Problem Melden',
      
      // Welcome Header
      goodMorning: 'Guten Morgen',
      goodAfternoon: 'Guten Tag',
      goodEvening: 'Guten Abend',
      claraReady: 'Clara ist bereit, Ihnen zu helfen',
      monthlyIncome: 'Monatliche Einnahmen',
      openTasks: 'Offene Aufgaben',
      activeTenants: 'Aktive Mieter',
      stopListening: 'Aufnahme stoppen',
      startListening: 'Aufnahme starten',
      disableSpeech: 'Sprachausgabe deaktivieren',
      enableSpeech: 'Sprachausgabe aktivieren',
      currentLanguage: 'Aktuelle Sprache',
      claraChat: 'Clara Chat',
      
      // Common UI
      loading: 'Lädt',
      online: 'Online',
      offline: 'Offline',
      viewAll: 'Alle Anzeigen',
      noRecentActivities: 'Keine Aktuellen Aktivitäten'
    },
    
    es: {
      // Navigation & Layout
      dashboard: 'Panel de Control',
      properties: 'Propiedades',
      tenants: 'Inquilinos',
      owners: 'Propietarios',
      finances: 'Finanzas',
      maintenance: 'Mantenimiento',
      documents: 'Documentos',
      communication: 'Comunicación',
      analytics: 'Análisis',
      settings: 'Configuración',
      tasks: 'Tareas',
      
      // Dashboard KPIs
      rentalIncome: 'Ingresos por Alquiler',
      units: 'Unidades',
      occupancy: 'Ocupación',
      monthly: 'Mensual',
      active: 'Activo',
      
      // Recent Activities
      newTenant: 'Nuevo Inquilino',
      newProperty: 'Nueva Propiedad',
      newUnit: 'Nueva Unidad',
      recentActivities: 'Actividades Recientes',
      
      // Quick Actions
      quickActions: 'Acciones Rápidas',
      addTenant: 'Agregar Inquilino',
      addProperty: 'Agregar Propiedad',
      uploadDocument: 'Subir Documento',
      reportIssue: 'Reportar Problema',
      
      // Welcome Header
      goodMorning: 'Buenos días',
      goodAfternoon: 'Buenas tardes',
      goodEvening: 'Buenas noches',
      claraReady: 'Clara está lista para ayudarte',
      monthlyIncome: 'Ingresos mensuales',
      openTasks: 'Tareas pendientes',
      activeTenants: 'Inquilinos activos',
      stopListening: 'Detener grabación',
      startListening: 'Iniciar grabación',
      disableSpeech: 'Desactivar voz',
      enableSpeech: 'Activar voz',
      currentLanguage: 'Idioma actual',
      claraChat: 'Chat Clara',
      
      // Common UI
      loading: 'Cargando',
      online: 'En Línea',
      offline: 'Desconectado',
      viewAll: 'Ver Todo',
      noRecentActivities: 'No Hay Actividades Recientes'
    },
    
    en: {
      // Navigation & Layout
      dashboard: 'Dashboard',
      properties: 'Properties',
      tenants: 'Tenants',
      owners: 'Owners',
      finances: 'Finances',
      maintenance: 'Maintenance',
      documents: 'Documents',
      communication: 'Communication',
      analytics: 'Analytics',
      settings: 'Settings',
      tasks: 'Tasks',
      
      // Dashboard KPIs
      rentalIncome: 'Rental Income',
      units: 'Units',
      occupancy: 'Occupancy',
      monthly: 'Monthly',
      active: 'Active',
      
      // Recent Activities
      newTenant: 'New Tenant',
      newProperty: 'New Property',
      newUnit: 'New Unit',
      recentActivities: 'Recent Activities',
      
      // Quick Actions
      quickActions: 'Quick Actions',
      addTenant: 'Add Tenant',
      addProperty: 'Add Property',
      uploadDocument: 'Upload Document',
      reportIssue: 'Report Issue',
      
      // Welcome Header
      goodMorning: 'Good morning',
      goodAfternoon: 'Good afternoon',
      goodEvening: 'Good evening',
      claraReady: 'Clara is ready to help you',
      monthlyIncome: 'Monthly income',
      openTasks: 'Open tasks',
      activeTenants: 'Active tenants',
      stopListening: 'Stop listening',
      startListening: 'Start listening',
      disableSpeech: 'Disable speech',
      enableSpeech: 'Enable speech',
      currentLanguage: 'Current language',
      claraChat: 'Clara Chat',
      
      // Common UI
      loading: 'Loading',
      online: 'Online',
      offline: 'Offline',
      viewAll: 'View All',
      noRecentActivities: 'No Recent Activities'
    }
  };

  // Initialisierung beim Start
  useEffect(() => {
    // Gespeicherte Sprache laden
    const savedLanguage = localStorage.getItem('clara360-language');
    if (savedLanguage && languages[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Übersetzungsfunktion
  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations['de'][key] || key;
  };

  // Sprache ändern
  const changeLanguage = (languageCode) => {
    if (languages[languageCode]) {
      setCurrentLanguage(languageCode);
      localStorage.setItem('clara360-language', languageCode);
      
      // Event für andere Komponenten senden
      window.dispatchEvent(new CustomEvent('clara360-language-change', {
        detail: { 
          language: languageCode,
          languageData: languages[languageCode]
        }
      }));
      
      // Clara über Sprachänderung informieren
      window.dispatchEvent(new CustomEvent('clara-language-update', {
        detail: { language: languageCode }
      }));
    }
  };

  // Währungsformatierung
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(
      currentLanguage === 'de' ? 'de-DE' : 
      currentLanguage === 'es' ? 'es-ES' : 'en-US', 
      {
        style: 'currency',
        currency: 'EUR'
      }
    ).format(amount);
  };

  // Datumsformatierung
  const formatDate = (date) => {
    return date.toLocaleDateString(
      currentLanguage === 'de' ? 'de-DE' : 
      currentLanguage === 'es' ? 'es-ES' : 'en-US', 
      {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    );
  };

  // Zeitformatierung
  const formatTime = (date) => {
    return date.toLocaleTimeString(
      currentLanguage === 'de' ? 'de-DE' : 
      currentLanguage === 'es' ? 'es-ES' : 'en-US', 
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  // Context Value
  const contextValue = {
    currentLanguage,
    languages,
    changeLanguage,
    t,
    formatCurrency,
    formatDate,
    formatTime
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook für Komponenten
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;

